import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DecimalGrid from '../DecimalGrid/DecimalGrid';
import MatchingGame from '../MatchingGame/MatchingGame';
import VerticalCalc from '../VerticalCalc/VerticalCalc';
import DiyImageShow from "../DiyImageShow/DiyImageShow";
import './WrongAnswerReview.css';
import { fetchMathApiWithAuth } from "../../utils/mathServerHttp";

// 定义题型常量
const QUESTION_TYPES = {
    CHOICE: 1,          // 选择题
    FILL_IN: 2,         // 填空题
    DECIMAL_GRID: 3,    // 小数格
    MATCHING: 4,        // 连线题
    VERTICAL_CALC: 5    // 竖式计算题
};

const removeDecimalPoint = (num, isHundred) => isHundred ? num * 100 : num * 10;

const WrongAnswerReview = () => {
    const [searchParams] = useSearchParams();
    const unitId = searchParams.get('unitId');
    const chapterId = searchParams.get('chapterId');

    // 错题重现信息
    const [wrongAnswerData, setWrongAnswerData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchWrongReviewData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const params = new URLSearchParams({ unitId });

            if (chapterId) {
                params.append("chapterId", chapterId);
            }

            const data = await fetchMathApiWithAuth(`/api/unit/errorReplay?${params.toString()}`);
            setWrongAnswerData(data);
        } catch (error) {
            console.error('Failed to fetch wrong review data:', error);
            setError('系统异常，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    // 加载错题重现
    useEffect(() => {
        fetchWrongReviewData();
    }, [unitId, chapterId]);

    const navigate = useNavigate();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [showWrongRecord, setShowWrongRecord] = useState(null);
    const [showAnswerExplanation, setShowAnswerExplanation] = useState(null);

    const handleGoBack = () => {
        navigate('/');
    };

    const handleNextQuestion = () => {
        if (wrongAnswerData.length > 0) {
            setCurrentQuestionIndex((prev) => (prev + 1) % wrongAnswerData.length);
            setShowWrongRecord(null);
            setShowAnswerExplanation(null);
        }
    };

    const handlePrevQuestion = () => {
        if (wrongAnswerData.length > 0) {
            setCurrentQuestionIndex((prev) => (prev - 1 + wrongAnswerData.length) % wrongAnswerData.length);
            setShowWrongRecord(null);
            setShowAnswerExplanation(null);
        }
    };

    const handleShowWrongRecord = (blankOrder) => {
        setShowWrongRecord(blankOrder);
        setShowAnswerExplanation(null);
    };

    const handleShowAnswerExplanation = (blankOrder) => {
        setShowAnswerExplanation(blankOrder);
    };

    if (isLoading) {
        return <div>加载中...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    if (!wrongAnswerData || wrongAnswerData.length === 0) {
        return <div>暂无数据</div>;
    }

    const currentQuestion = wrongAnswerData[currentQuestionIndex];

    // 解析填空题结构 (仅针对 type=2)
    const parseFillInTheBlank = (content) => {
        const regex = /#\{(\d+),(\d+)\}/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(content))!== null) {
            const [placeholder, blankOrder, maxLength] = match;
            const index = match.index;
            if (index > lastIndex) {
                parts.push(content.substring(lastIndex, index));
            }
            const correctAnswer = currentQuestion?.correctAnswers.find(ans => ans.blankOrder === Number(blankOrder));
            const wrongRecord = currentQuestion?.wrongAnswerRecords.find(record => record.blankOrder === Number(blankOrder));
            const isWrong = wrongRecord!== undefined;
            parts.push({
                type: 'input',
                blankOrder: Number(blankOrder),
                maxLength: Number(maxLength),
                correctAnswer: correctAnswer? correctAnswer.answer : '',
                isWrong
            });
            lastIndex = index + placeholder.length;
        }

        if (lastIndex < content.length) {
            parts.push(content.substring(lastIndex));
        }
        return parts;
    };

    const { questionVO, correctAnswers, wrongAnswerRecords } = currentQuestion;

    return (
        <div className="wrong-answer-review-page">
            {/* 顶部栏 */}
            <div className="paper-header">
                <button
                    className="paper-back-button"
                    onClick={handleGoBack}
                    aria-label="返回"
                >
                    <svg className="paper-close-icon" viewBox="0 0 24 24" fill="none">
                        <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor"
                              strokeWidth="2" strokeLinecap="round"/>
                        <line x1="20" y1="4" x2="4" y2="20" stroke="currentColor"
                              strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </button>

                <div className="paper-header-right">
                    <div className="paper-progress-container">
                        <div
                            className="paper-progress-fill"
                            style={{ width: `${((currentQuestionIndex + 1) / wrongAnswerData.length) * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* 新增的错题展示头部栏 */}
            <div className="wrong-answer-header">
                <h1 className="wrong-answer-header-title">温故而知新</h1>
            </div>

            <div className="paper-container">
                {/* (选择题 / 小数格 / 连线 / 竖式 / 自定义图像) => 直接显示 content */}
                {[QUESTION_TYPES.CHOICE, QUESTION_TYPES.DECIMAL_GRID, QUESTION_TYPES.MATCHING, QUESTION_TYPES.VERTICAL_CALC, QUESTION_TYPES.DIY_IMAGE].includes(questionVO.type) && (
                    <div className="paper-question-title-area">
                        <div className="paper-question-content">
                            {questionVO.content}
                        </div>
                    </div>
                )}


                {/* 选择题 */}
                {questionVO.type === QUESTION_TYPES.CHOICE && (
                    <div className="paper-option-list">
                        {questionVO.options?.map((option) => {
                            const isCorrect = correctAnswers.some(ans => ans.answer === option.name);
                            const isWrong = wrongAnswerRecords.some(record => record.answer === option.name);
                            return (
                                <div
                                    key={option.name}
                                    onClick={isWrong? () => handleShowWrongRecord(1) : null}
                                    className={`paper-option-button ${isCorrect? 'correct' : ''} ${isWrong? 'wrong' : ''}`}
                                >
                                    {option.name}. {option.content}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* 填空题 => 解析填空结构 */}
                {questionVO.type === QUESTION_TYPES.FILL_IN && (
                    <div className="paper-question-title-area">
                        <div className="paper-question-content">
                            {parseFillInTheBlank(questionVO.content).map((part, idx) => {
                                if (typeof part === 'string') {
                                    return <span key={idx}>{part}</span>;
                                } else if (part.type === 'input') {
                                    const blankOrder = part.blankOrder;
                                    const isWrong = part.isWrong;
                                    const value = part.correctAnswer;
                                    return (
                                        <span
                                            key={idx}
                                            className="paper-fill-wrapper"
                                            onClick={isWrong? () => handleShowWrongRecord(blankOrder) : null}
                                        >
                                        <span className="paper-left-paren">(</span>
                                        <span
                                            className={
                                                isWrong
                                                    ? "paper-fill-display paper-fill-wrong"
                                                    : "paper-fill-display"
                                            }
                                            style={{width: `${part.maxLength}ch`}}
                                        >
                                            {isWrong? "错" : value}
                                        </span>
                                        <span className="paper-right-paren">)</span>
                                    </span>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </div>
                )}

                {/* 竖式计算图 */}
                {questionVO.showTime &&
                    questionVO.showTime.type ==='vertical_calculate' && (
                        <div className="paper-question-show-time-area">
                            <VerticalCalc
                                dataMatrix={questionVO.showTime.lines || []}
                                correctAnswers={questionVO.type!== QUESTION_TYPES.VERTICAL_CALC? [] : correctAnswers}
                                wrongAnswerRecords={questionVO.type!== QUESTION_TYPES.VERTICAL_CALC? [] : wrongAnswerRecords}
                                onWrongClick={questionVO.type!== QUESTION_TYPES.VERTICAL_CALC? () => {} : handleShowWrongRecord} // 传递回调函数
                            />
                        </div>
                    )
                }

                {/* 连线图 */}
                {questionVO.showTime &&
                    questionVO.showTime.type === 'link_link' && (
                        <div className="paper-question-show-time-area">
                            <MatchingGame
                                leftItems={questionVO.showTime.left}
                                rightItems={questionVO.showTime.right}
                                wrongAnswerRecords={wrongAnswerRecords}
                                onWrongItemClick={handleShowWrongRecord}
                            />
                        </div>
                    )}

                {/* 小数格 */}
                {questionVO.showTime &&
                    questionVO.showTime.type === "decimal_grid" &&
                    questionVO.showTime.params &&
                    questionVO.showTime.params.length >= 3 && (
                        <div className="paper-showtime-grid"
                             onClick={questionVO.type!== QUESTION_TYPES.DECIMAL_GRID? null
                                 : () => handleShowWrongRecord(1)}>
                            <DecimalGrid
                                gridType={questionVO.showTime.params[0]}
                                filledCount={
                                    questionVO.type!== QUESTION_TYPES.DECIMAL_GRID? Number(questionVO.showTime.params[1])
                                        : (Number(questionVO.showTime.params[0]) == 100?
                                            removeDecimalPoint(Number(wrongAnswerRecords[0].answer), true) :
                                            removeDecimalPoint(Number(wrongAnswerRecords[0].answer), false))
                                }
                                gridModify={false}
                            />
                        </div>
                    )}

                {/* 自定义图像 */}
                {questionVO.showTime &&
                    questionVO.showTime.type === "diy_image" && (
                        <div className="paper-question-show-time-area">
                            <DiyImageShow savedData={questionVO.showTime} showGrid={false} />
                        </div>
                    )
                }

                {/* 答错记录弹窗 */}
                {showWrongRecord && (
                    <div className="wrong-answer-record-modal">
                        <div className="wrong-answer-record-header">
                            <span className="wrong-answer-record-icon">❌</span>
                            <span className="wrong-answer-record-text">答错记录</span>
                        </div>
                        <ul>
                            {wrongAnswerRecords.filter(record => record.blankOrder === showWrongRecord).map((record, idx) => (
                                <li key={idx}>{record.answer}</li>
                            ))}
                        </ul>
                        <button
                            className="wrong-answer-record-confirm-button"
                            onClick={() => handleShowAnswerExplanation(showWrongRecord)}
                        >
                            查看答案
                        </button>
                    </div>
                )}

                {/* 答案解析弹窗 */}
                {showAnswerExplanation && (
                    <div className="wrong-answer-explanation-modal">
                        <div className="wrong-answer-explanation-header">
                            <span className="wrong-answer-explanation-icon">✅</span>
                            <span className="wrong-answer-explanation-text">答案解析</span>
                        </div>
                        <p className="wrong-answer-correct">
                            正确答案：{correctAnswers.find(ans => ans.blankOrder === showAnswerExplanation).answer}
                        </p>
                        <p className="wrong-answer-explanation">
                            答案解析：{correctAnswers.find(ans => ans.blankOrder === showAnswerExplanation).explanation}
                        </p>
                        <button
                            className="wrong-answer-explanation-confirm-button"
                            onClick={() => setShowAnswerExplanation(null)}
                        >
                            我知道了
                        </button>
                    </div>
                )}
            </div>

            {/* 左右换题按钮 */}
            <div className="question-switch-buttons">
                <button className="prev-question-button" onClick={handlePrevQuestion}>
                    &lt;
                </button>
                <button className="next-question-button" onClick={handleNextQuestion}>
                    &gt;
                </button>
            </div>
        </div>
    );
};

export default WrongAnswerReview;